import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { of, Subject } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { BillingService } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

interface DialogData {
  productName: string;
  customerName: string;
  merchantId: string;
  customerId: string;
  sku: string;
  orderId: string;
  currentUnitPrice: number;
}

@Component({
  selector: 'billing-ui-change-price-dialog',
  templateUrl: './change-price-dialog.component.html',
  styleUrls: ['./change-price-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChangePriceDialogComponent {
  loading$$: Subject<boolean> = new Subject<boolean>();
  productName: string;
  customerName: string;
  merchantId: string;
  customerId: string;
  sku: string;
  orderId: string;
  currentUnitPrice: number;
  formGroup: UntypedFormGroup;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private billingService: BillingService,
    private alertService: SnackbarService,
    public dialogRef: MatDialogRef<ChangePriceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.productName = data.productName;
    this.customerName = data.customerName;
    this.merchantId = data.merchantId;
    this.customerId = data.customerId;
    this.sku = data.sku;
    this.orderId = data.orderId;
    this.currentUnitPrice = this.data.currentUnitPrice / 100;
    this.formGroup = this.formBuilder.group({
      currentUnitPrice: [this.data.currentUnitPrice / 100, Validators.min(0)],
    });
  }

  public submit(): void {
    let { currentUnitPrice } = this.formGroup.getRawValue();
    if (currentUnitPrice > 0) {
      currentUnitPrice = currentUnitPrice * 100;
    }
    this.loading$$.next(true);
    this.billingService
      .changeBillableItemCustomUnitPrice(this.merchantId, this.sku, this.customerId, this.orderId, currentUnitPrice)
      .pipe(
        take(1),
        map(() => true),
        catchError(() => of(false)),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe((isSuccess) => {
        if (isSuccess) {
          this.alertService.openSuccessSnack('Successfully updated unit price');
          this.dialogRef.close(isSuccess);
        } else {
          this.alertService.openErrorSnack('Failed to updated unit price');
        }
      });
  }
}
